/**
 * DNS 泄露检测类
 * 检测用户的 DNS 请求是否泄露了真实 IP 地址
 */
class DNSLeakDetector {
  constructor() {
    this.ednsApiUrl = 'http://edns.ip-api.com/json';
    this.ipApiUrl = 'https://ip.nezhavpn.uk/ip-api';
  }

  /**
   * 检测 DNS 泄露
   * @returns {Promise<Object>} 包含检测结果的对象
   */
  async detectLeaks() {
    try {
      // 获取用户的 IP 信息
      const userIpInfo = await this.fetchUserIpInfo();
      
      // 获取 DNS 解析器的 IP 信息
      const dnsInfo = await this.fetchDnsInfo();
      
      // 判断是否存在 DNS 泄露
      const isLeaking = this.checkForLeak(userIpInfo, dnsInfo);
      
      return {
        leakDetected: isLeaking,
        userIpInfo: userIpInfo,
        detectedServers: dnsInfo ? [dnsInfo] : []
      };
    } catch (error) {
      console.error('DNS 泄露检测失败:', error);
      return {
        leakDetected: false,
        error: error.message,
        detectedServers: []
      };
    }
  }

  /**
   * 获取用户的 IP 信息
   * @returns {Promise<Object>} 用户的 IP 信息
   */
  async fetchUserIpInfo() {
    try {
      const response = await fetch(this.ipApiUrl);
      if (!response.ok) {
        throw new Error(`HTTP 错误: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('获取用户 IP 信息失败:', error);
      return null;
    }
  }

  /**
   * 获取 DNS 解析器的信息
   * @returns {Promise<Object>} DNS 解析器的信息
   */
  async fetchDnsInfo() {
    try {
      const response = await fetch(this.ednsApiUrl);
      if (!response.ok) {
        throw new Error(`HTTP 错误: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.dns) {
        return null;
      }

      // 获取 DNS IP 的地理位置信息
      const dnsIpResponse = await fetch(`${this.ipApiUrl}?ip=${data.dns.ip}`);
      if (!dnsIpResponse.ok) {
        throw new Error(`获取 DNS IP 地理位置信息失败: ${dnsIpResponse.status}`);
      }
      const dnsIpInfo = await dnsIpResponse.json();
      
      return {
        ip: data.dns.ip,
        countryCode: dnsIpInfo.countryCode,
        country: dnsIpInfo.country,
        geo: data.dns.geo,
        provider: dnsIpInfo.isp || 'Unknown'
      };
    } catch (error) {
      console.error('获取 DNS 信息失败:', error);
      return null;
    }
  }

  /**
   * 检查是否存在 DNS 泄露
   * @param {Object} userIpInfo 用户的 IP 信息
   * @param {Object} dnsInfo DNS 解析器的信息
   * @returns {boolean} 是否检测到 DNS 泄露
   */
  checkForLeak(userIpInfo, dnsInfo) {
    // 如果无法获取用户 IP 信息或 DNS 信息，无法判断是否泄露
    if (!userIpInfo || !dnsInfo) {
      return false;
    }
    
    // 比较用户 IP 和 DNS 服务器的国家代码
    const userCountryCode = userIpInfo.countryCode || '';
    const dnsCountryCode = dnsInfo.countryCode || '';
    
    // 检查 DNS 服务器国家与用户 IP 国家是否匹配
    // 如果不匹配且用户使用代理/VPN，表示可能存在 DNS 泄露
    const countryMismatch = userCountryCode !== dnsCountryCode && userCountryCode !== '';
    
    // 检查 DNS 服务器 IP 与用户 IP 是否匹配
    // 处理 ISP 的 DNS 服务器可能与用户 IP 不同但位于同一国家的情况
    const isSameIsp = userIpInfo.isp && dnsInfo.provider && 
                     userIpInfo.isp.includes(dnsInfo.provider);
    
    // 特殊情况：如果是同一 ISP 的 DNS 服务器，可以认为不存在泄露
    if (isSameIsp) {
      return false;
    }
    
    // 如果用户可能使用代理/VPN（proxy 或 hosting 字段为 true）
    // 并且 DNS 服务器国家与用户 IP 国家不匹配，则认为存在泄露
    if ((userIpInfo.proxy || userIpInfo.hosting) && countryMismatch) {
      return true;
    }
    
    // 否则根据国家匹配情况判断
    return countryMismatch;
  }
}

export default DNSLeakDetector; 